// apps/web/src/lib/aiShotJob.ts
//
// The save state for an AI screenshot read: the server-side job id, plus small
// thumbnails of what was uploaded.
//
// A read takes about a minute — longer than iOS keeps a backgrounded web view
// alive — so it cannot be held in React state. It lives here instead, which is
// what lets the result be collected after the modal was closed, the tab was
// reloaded, or the phone was locked and the whole web view suspended.
//
// This deliberately outlives the *finished* read too, not just the in-flight one.
// A read that resolved to an exercise the catalog does not have leaves the user
// waiting on an admin to approve it, which takes as long as it takes; the modal
// is conditionally rendered, so closing it unmounts everything. Since the job row
// holds the parsed workout server-side for a week anyway, keeping the id is the
// whole of what is needed to walk back into the preview — and re-uploading would
// otherwise cost another AI credit for a result the server still has.
//
// Shared rather than inlined in the modal because the push-tap handler writes the
// same entry, and two copies of a storage shape drift.

const KEY = 'probell.aiShotJob';
/**
 * Matches SCREENSHOT_JOB_TTL_MS in apps/api/src/routes/aiPlan.ts. Past it the
 * server has deleted the job, and the GET's 404 path clears this entry anyway —
 * so a longer window here would only produce ids that resolve to nothing.
 */
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

export type ShotJobSave = {
  jobId: string;
  startedAt: number;
  /** Data URLs, one per uploaded image. Empty when they could not be stored. */
  thumbs: string[];
};

function write(entry: ShotJobSave): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(entry));
  } catch {
    // Almost always the quota, and the thumbnails are all that could have blown
    // it. The id is what makes the read recoverable, so drop the pictures and
    // keep it rather than losing both.
    try {
      localStorage.setItem(KEY, JSON.stringify({ ...entry, thumbs: [] }));
    } catch {
      // Private mode, or storage full of other things. The in-memory poll still
      // covers this session.
    }
  }
}

export function rememberShotJob(jobId: string, thumbs: string[] = []): void {
  // Re-remembering the id already stored must not throw its thumbnails away, and
  // must not restart its clock: the push-tap handler writes the same id again on
  // a cold launch, and the TTL is the server's, counted from when the read began.
  const existing = readShotJob();
  if (existing && existing.jobId === jobId) {
    write({ ...existing, thumbs: thumbs.length ? thumbs : existing.thumbs });
    return;
  }
  write({ jobId, startedAt: Date.now(), thumbs });
}

/** Attach thumbnails to the entry already written for `jobId`, if it is still the current one. */
export function rememberShotThumbs(jobId: string, thumbs: string[]): void {
  const existing = readShotJob();
  if (!existing || existing.jobId !== jobId) return;
  write({ ...existing, thumbs });
}

export function readShotJob(): ShotJobSave | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.jobId) return null;
    const startedAt = Number(parsed.startedAt) || 0;
    if (Date.now() - startedAt > MAX_AGE_MS) {
      localStorage.removeItem(KEY);
      return null;
    }
    return {
      jobId: String(parsed.jobId),
      startedAt,
      thumbs: Array.isArray(parsed.thumbs) ? parsed.thumbs.map(String) : [],
    };
  } catch {
    return null;
  }
}

export function forgetShotJob(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* nothing to clean up */
  }
}
